// アプリ全体の状態と購読。UI フレームワークを使わないため、
// 「状態を変えたら listeners を呼ぶ」だけの最小ストアにする。

import { loadFmAvatar } from "../core/fmavatar.ts";
import { applyClipPack, buildClipPack, clipPackFileName, parseClipPack } from "../core/clipPack.ts";
import {
  DEFAULT_TRACKING,
  type ApplyOptions,
  type BlendshapeValue,
  type ClipKeyframe,
  type MenuEntry,
  emptyProjectState,
  normalizeProjectState,
  parseShapeKey,
  shapeKeyOf,
  trackingOf,
  type FaceClip,
  type Hand,
  type LoadedAvatar,
  type ProjectState,
  type TrackingSetting,
} from "../core/types.ts";
import { describeError } from "../core/errors.ts";
import * as db from "./db.ts";
import { AvatarViewer } from "../render/viewer.ts";
import { downloadBytes } from "../ui/dom.ts";

export type TabId = "avatar" | "expression" | "gesture" | "export";

/** ジェスチャー画面でプレビュー中の対象。基本スロットか、コンビネーションのセル。 */
export type PreviewTarget =
  | { kind: "hand"; hand: Hand; gesture: number }
  | { kind: "combo"; left: number; right: number }
  | { kind: "menu"; index: number }
  | { kind: "clip"; clipIndex: number };

export interface EditorState {
  /** 編集中のシェイプキー値 shapeKeyOf() → 0〜100 */
  values: Map<string, number>;
  /** 編集中クリップの index。新規なら null */
  editingClipIndex: number | null;
  filter: string;
  /** 編集中の Tracking Control（保存時にクリップへ書き込む） */
  tracking: TrackingSetting;
  /** キーフレーム追加時の時刻（秒） */
  keyframeTime: number;
}

export class Store {
  avatar: LoadedAvatar | null = null;
  project: ProjectState | null = null;
  savedAvatars: db.StoredAvatar[] = [];
  tab: TabId = "avatar";
  editor: EditorState = { values: new Map(), editingClipIndex: null, filter: "", tracking: { ...DEFAULT_TRACKING }, keyframeTime: 0 };
  /** ジェスチャー画面でプレビュー中のスロット */
  previewing: PreviewTarget | null = null;
  /** 表情タブのキーフレーム編集で「▶ 再生」中のクリップ index（永続化しない） */
  playingClipIndex: number | null = null;
  /** 再生開始時刻（秒）。「頭から再生」は0、「現在位置から再生」はスクラブ位置。 */
  playStartTime = 0;
  /** 再生開始のたびに増分し、preview.ts 側で再生位置をリセットするトリガーにする。 */
  playSession = 0;
  persistenceAvailable = true;
  banner: { kind: "error" | "info"; title: string; detail: string; hint: string | null } | null = null;

  private listeners = new Set<() => void>();

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  notify(): void {
    for (const fn of this.listeners) fn();
  }

  setTab(tab: TabId): void {
    if (tab !== this.tab) this.setBanner(null);
    this.tab = tab;
    this.notify();
  }

  private bannerTimer: ReturnType<typeof setTimeout> | null = null;

  /** 詳細のない info バナー（読み込み完了などの簡易通知）は一定時間で自動的に消える。
   *  詳細付き（注意事項など）と error は読む時間が必要なので消えず、手動で閉じる */
  setBanner(b: Store["banner"]): void {
    if (this.bannerTimer !== null) {
      clearTimeout(this.bannerTimer);
      this.bannerTimer = null;
    }
    this.banner = b;
    this.notify();
    if (b?.kind === "info" && b.detail === "") {
      this.bannerTimer = setTimeout(() => {
        this.bannerTimer = null;
        this.banner = null;
        this.notify();
      }, 5000);
    }
  }

  // --- アバター ------------------------------------------------------------

  async refreshSavedList(): Promise<void> {
    if (!this.persistenceAvailable) return;
    try {
      this.savedAvatars = await db.listAvatars();
    } catch {
      this.persistenceAvailable = false;
    }
    this.notify();
  }

  /**
   * インポートしようとしているファイルと中身が完全一致する保存済みエントリを探す。
   * 見つかった場合、呼び出し側で「上書きしますか？」の確認を出す想定。
   */
  async findDuplicateByHash(bytes: Uint8Array): Promise<db.StoredAvatar | undefined> {
    if (!this.persistenceAvailable) return undefined;
    try {
      const hash = await db.hashBytes(bytes);
      return await db.findAvatarByHash(hash);
    } catch {
      return undefined;
    }
  }

  /**
   * zip バイト列を読み込んで現在のアバターにする。保存も行う。
   * @param reuseId 保存済みエントリを開き直す場合、そのエントリの id。未指定なら新規インポートとして扱う。
   */
  async loadFromBytes(bytes: Uint8Array, persist: boolean, reuseId?: string): Promise<void> {
    // 連続切替で古い呼び出しの await 後の処理が新しい状態を上書きしないよう、呼び出しごとにトークンを発行する
    const token = ++this.loadToken;
    const avatar = loadFmAvatar(bytes);
    const storeId = reuseId ?? avatar.id;
    avatar.id = storeId;

    let project: ProjectState | undefined;
    if (this.persistenceAvailable) {
      try {
        if (reuseId) {
          // 保存済みエントリを開く: そのエントリ自身の作業を読む
          project = await db.getProject<ProjectState>(reuseId);
        } else if (avatar.manifest.avatarUid) {
          // 新規インポート: 同じ avatarUid を持つ既存エントリのうち最新のものから作業を引き継ぐ
          const prevVersions = await db.listAvatarsByUid(avatar.manifest.avatarUid);
          const latest = prevVersions[0]; // listAvatarsByUid は listAvatars 由来で savedAt 降順
          if (latest) project = await db.getProject<ProjectState>(latest.id);
        }
      } catch {
        /* 読めなければ新規 */
      }
    }
    if (token !== this.loadToken) return; // より新しい切替が始まっている
    // 旧バージョンで保存されたプロジェクトは欠けているフィールドを補完する
    let nextProject: ProjectState;
    if (project) {
      nextProject = normalizeProjectState(project);
      nextProject.avatarId = storeId;
      // 引き継いだプロジェクトにも、新しく増えた既存クリップがあれば取り込む
      this.importClipsInto(nextProject, avatar);
    } else {
      nextProject = this.projectFromFace(avatar);
      nextProject.avatarId = storeId;
    }

    this.avatar = avatar;
    this.project = nextProject;
    this.editor = { values: new Map(), editingClipIndex: null, filter: "", tracking: { ...DEFAULT_TRACKING }, keyframeTime: 0 };
    this.previewing = null;
    this.playingClipIndex = null;
    this.playStartTime = 0;

    if (persist && this.persistenceAvailable) {
      try {
        const hash = await db.hashBytes(bytes);
        let thumbnail: string | undefined;
        try {
          thumbnail = (await AvatarViewer.captureThumbnail(avatar)) ?? undefined;
        } catch {
          thumbnail = undefined;
        }
        await db.putAvatar({
          id: storeId,
          name: avatar.manifest.avatarName,
          zip: bytes,
          savedAt: new Date().toISOString(),
          avatarUid: avatar.manifest.avatarUid ?? null,
          hash,
          thumbnail,
        });
        await db.putProject(nextProject);
        // 古い呼び出しがアクティブ ID を巻き戻さないようにする
        if (token === this.loadToken) await db.setActiveAvatarId(storeId);
      } catch {
        this.persistenceAvailable = false;
      }
    }
    await this.refreshSavedList();
    this.notify();
  }

  /** loadFromBytes の呼び出し世代。最新の呼び出しだけが状態を反映する */
  private loadToken = 0;

  /**
   * face.json の既存クリップをプロジェクトへ取り込む。
   * すでに同じ assetGuid（無ければ同名）のクリップがあるものはスキップする。
   * @returns existingClips[].index → project.clips[].index の対応と、新規追加数
   */
  private importClipsInto(p: ProjectState, avatar: LoadedAvatar): { refToIndex: Map<number, number>; added: number } {
    const nameSets = new Map(avatar.face.meshes.map((m) => [m.index, new Set(m.blendshapeNames)]));
    const refToIndex = new Map<number, number>();
    const byGuid = new Map(p.clips.filter((c) => c.assetGuid).map((c) => [c.assetGuid as string, c.index]));
    const byName = new Map(p.clips.map((c) => [c.name, c.index]));
    let added = 0;

    for (const ec of avatar.face.existingClips) {
      // guid を持つクリップは guid のみで照合する（同名でも guid が違えば別クリップ）
      const dup = ec.assetGuid ? byGuid.get(ec.assetGuid) : byName.get(ec.name);
      if (dup !== undefined) {
        refToIndex.set(ec.index, dup);
        continue;
      }
      // 存在しないシェイプキーを参照する項目は落とす（読み込み時に警告済み）
      const shapes = ec.blendshapes.filter((b) => nameSets.get(b.meshIndex)?.has(b.name));
      const name = this.uniqueName(p.clips, ec.name);
      const idx = p.clips.reduce((max, c) => Math.max(max, c.index), -1) + 1;
      p.clips.push({
        index: idx,
        name,
        type: "static",
        durationSeconds: 0.1,
        assetGuid: ec.assetGuid ?? null,
        shapes,
      });
      byName.set(name, idx);
      if (ec.assetGuid) byGuid.set(ec.assetGuid, idx);
      refToIndex.set(ec.index, idx);
      added++;
    }
    return { refToIndex, added };
  }

  /** face.json の既存解析結果を初期状態に反映する */
  private projectFromFace(avatar: LoadedAvatar): ProjectState {
    const p = emptyProjectState(avatar.id);
    const { refToIndex } = this.importClipsInto(p, avatar);

    // 既存ジェスチャー割り当てを初期表示に反映
    for (const hand of ["left", "right"] as const) {
      for (const e of avatar.face.existingGestures[hand]) {
        const mapped = refToIndex.get(e.clipRef);
        if (mapped !== undefined && e.gesture >= 0 && e.gesture <= 7) {
          p.gestures[hand][e.gesture] = mapped;
        }
      }
    }
    // 既存コンビネーションも取り込む
    for (const c of avatar.face.existingGestures.combos) {
      const mapped = refToIndex.get(c.clipRef);
      if (mapped === undefined) continue;
      if (c.left < 0 || c.left > 7 || c.right < 0 || c.right > 7) continue;
      if (p.combos.some((x) => x.left === c.left && x.right === c.right)) continue;
      p.combos.push({ left: c.left, right: c.right, clipIndex: mapped });
    }
    return p;
  }

  /**
   * 「既存クリップを取り込む」ボタン用。読み込み直後に自動取り込み済みなので、
   * クリップを削除したあとに戻したい場合や、取り込みを取りこぼした場合の再実行用。
   * @returns 新たに追加した件数
   */
  importExistingClips(): number {
    if (!this.project || !this.avatar) return 0;
    const { added } = this.importClipsInto(this.project, this.avatar);
    if (added > 0) this.persist();
    return added;
  }

  /** face.json にあってプロジェクトにまだ無い既存クリップの数 */
  pendingExistingClipCount(): number {
    if (!this.project || !this.avatar) return 0;
    const guids = new Set(this.project.clips.filter((c) => c.assetGuid).map((c) => c.assetGuid));
    const names = new Set(this.project.clips.map((c) => c.name));
    return this.avatar.face.existingClips.filter(
      (ec) => (ec.assetGuid ? !guids.has(ec.assetGuid) : !names.has(ec.name)),
    ).length;
  }

  private uniqueName(clips: FaceClip[], base: string): string {
    const clean = (base.replace(/[\\/:*?"<>|]/g, "_").trim() || "クリップ").slice(0, 200);
    const used = new Set(clips.map((c) => c.name));
    if (!used.has(clean)) return clean;
    for (let i = 2; ; i++) {
      const cand = `${clean} ${i}`;
      if (!used.has(cand)) return cand;
    }
  }

  async loadSaved(id: string): Promise<void> {
    const rec = await db.getAvatar(id);
    if (!rec) throw new Error("保存されたアバターが見つかりません。");
    await this.loadFromBytes(rec.zip, true, id);
  }

  async deleteSaved(id: string): Promise<void> {
    await db.deleteAvatar(id);
    if (this.avatar?.id === id) {
      this.avatar = null;
      this.project = null;
      await db.setActiveAvatarId(null);
    }
    await this.refreshSavedList();
  }

  /** 起動時に前回のアバターを復元する */
  async restore(): Promise<void> {
    this.persistenceAvailable = await db.isAvailable();
    if (!this.persistenceAvailable) {
      this.notify();
      return;
    }
    await this.refreshSavedList();
    const id = await db.getActiveAvatarId();
    if (!id) return;
    const rec = await db.getAvatar(id);
    if (!rec) return;
    try {
      // 保存済みエントリとして開き直す（avatar.id を保存時の ID のまま維持する）
      await this.loadFromBytes(rec.zip, false, id);
    } catch (e) {
      // 保存データは消さずに残し、エラー通知に留める（ユーザーが一覧から削除できる）
      const d = describeError(e);
      this.setBanner({
        kind: "error",
        title: `前回のアバター「${rec.name}」を復元できませんでした`,
        detail: d.detail,
        hint: d.hint ?? "保存データは残っています。不要なら一覧から削除してください。",
      });
    }
  }

  private async saveProject(): Promise<void> {
    if (!this.project || !this.persistenceAvailable) return;
    try {
      this.project.updatedAt = new Date().toISOString();
      await db.putProject(this.project);
    } catch {
      this.persistenceAvailable = false;
    }
  }

  // --- 表情エディタ --------------------------------------------------------

  setShapeValue(meshIndex: number, name: string, value: number): void {
    const key = shapeKeyOf(meshIndex, name);
    if (value === 0) this.editor.values.delete(key);
    else this.editor.values.set(key, value);
    this.notify();
  }

  clearShapeValues(): void {
    this.editor.values.clear();
    this.editor.editingClipIndex = null;
    this.editor.tracking = { ...DEFAULT_TRACKING };
    this.notify();
  }

  /** 編集中クリップの Tracking Control を変更する（保存済みクリップなら即反映） */
  setEditorTracking(patch: Partial<TrackingSetting>): void {
    this.editor.tracking = { ...this.editor.tracking, ...patch };
    const editing = this.clipByIndex(this.editor.editingClipIndex);
    if (editing) {
      editing.tracking = { ...this.editor.tracking };
      this.persist();
    } else {
      this.notify();
    }
  }

  /** 現在のスライダー値をクリップとして保存（新規 or 上書き） */
  saveAsClip(name: string): FaceClip {
    if (!this.project) throw new Error("アバターが読み込まれていません。");
    const shapes = [...this.editor.values.entries()]
      .map(([key, value]) => ({ ...parseShapeKey(key), value }))
      .filter((s) => s.value !== 0);

    const editing = this.editor.editingClipIndex;
    const existing = editing === null ? undefined : this.project.clips.find((c) => c.index === editing);

    if (existing) {
      existing.name = name;
      // keyframe クリップの shapes は最初のキーフレームと同期した代表ポーズなので、
      // スライダー値で上書きしない（キーフレームは addKeyframe で記録する）
      if (existing.type !== "keyframe") existing.shapes = shapes;
      existing.tracking = { ...this.editor.tracking };
      this.persist();
      return existing;
    }
    const index = this.project.clips.reduce((max, c) => Math.max(max, c.index), -1) + 1;
    const clip: FaceClip = {
      index, name, type: "static", durationSeconds: 0.1, assetGuid: null, shapes,
      tracking: { ...this.editor.tracking },
    };
    this.project.clips.push(clip);
    this.editor.editingClipIndex = index;
    this.persist();
    return clip;
  }

  editClip(index: number): void {
    if (!this.project) return;
    const clip = this.project.clips.find((c) => c.index === index);
    if (!clip) return;
    this.editor.values = new Map(clip.shapes.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
    this.editor.editingClipIndex = index;
    this.editor.tracking = trackingOf(clip);
    this.tab = "expression";
    this.notify();
  }

  /**
   * 既存クリップの内容を引き継いだ「新規クリップ」として編集を始める。
   * まだ保存はしないので、保存時に別名で新しいクリップになる。
   */
  startNewFromClip(index: number): void {
    if (!this.project) return;
    const clip = this.project.clips.find((c) => c.index === index);
    if (!clip) return;
    this.editor.values = new Map(clip.shapes.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
    this.editor.editingClipIndex = null;
    this.editor.tracking = trackingOf(clip);
    this.tab = "expression";
    this.notify();
  }

  duplicateClip(index: number): void {
    if (!this.project) return;
    const clip = this.project.clips.find((c) => c.index === index);
    if (!clip) return;
    const newIndex = this.project.clips.reduce((max, c) => Math.max(max, c.index), -1) + 1;
    this.project.clips.push({
      ...clip,
      index: newIndex,
      name: this.uniqueName(this.project.clips, `${clip.name} のコピー`),
      assetGuid: null,
      shapes: clip.shapes.map((s) => ({ ...s })),
      tracking: trackingOf(clip),
    });
    this.persist();
  }

  deleteClip(index: number): void {
    if (!this.project) return;
    this.project.clips = this.project.clips.filter((c) => c.index !== index);
    for (const hand of ["left", "right"] as const) {
      this.project.gestures[hand] = this.project.gestures[hand].map((v) => (v === index ? null : v));
    }
    this.project.combos = this.project.combos.filter((c) => c.clipIndex !== index);
    // メニュー項目の clipIndex: null は「表情なし（素の顔）」という別の意味を持つため、
    // null 化せず参照している項目ごと削除する
    const removedMenu = new Set(this.project.menu.filter((m) => m.clipIndex === index).map((m) => m.index));
    this.project.menu = this.project.menu.filter((m) => m.clipIndex !== index);
    const pv = this.previewing;
    if ((pv?.kind === "clip" && pv.clipIndex === index) || (pv?.kind === "menu" && removedMenu.has(pv.index))) {
      this.previewing = null;
    }
    if (this.playingClipIndex === index) this.playingClipIndex = null;
    if (this.editor.editingClipIndex === index) this.editor.editingClipIndex = null;
    this.persist();
  }

  clipByIndex(index: number | null): FaceClip | null {
    if (index === null || !this.project) return null;
    return this.project.clips.find((c) => c.index === index) ?? null;
  }

  /** クリップ名がその名前で使えるか（重複チェック。編集中クリップ自身は除く） */
  isNameAvailable(name: string, exceptIndex: number | null): boolean {
    if (!this.project) return false;
    return !this.project.clips.some((c) => c.name === name && c.index !== exceptIndex);
  }

  suggestClipName(): string {
    if (!this.project) return "表情1";
    return this.uniqueName(this.project.clips, `表情${this.project.clips.length + 1}`);
  }

  // --- クリップの個別/複数エクスポート・インポート -----------------------------

  /** 指定 index のクリップをシェイプキー名ベースの JSON に変換してダウンロードさせる */
  exportClipsAsPack(indices: number[]): void {
    if (!this.project) return;
    const clips = this.project.clips.filter((c) => indices.includes(c.index));
    if (clips.length === 0) return;
    const pack = buildClipPack(clips);
    const bytes = new TextEncoder().encode(JSON.stringify(pack, null, 2));
    const baseName = clips.length === 1 ? clips[0].name : `${this.avatar?.manifest.avatarName ?? "avatar"}_表情パック`;
    downloadBytes(bytes, clipPackFileName(baseName), "application/json");
  }

  /** クリップパック JSON を読み込み、現在のプロジェクトへ追加する */
  importClipPack(json: string): { addedCount: number; missingShapeNames: string[] } {
    if (!this.project || !this.avatar) throw new Error("アバターが読み込まれていません。");
    // 構造が不正なら FmError を投げ、何も取り込まない
    const pack = parseClipPack(json);
    const avatarMeshes = this.avatar.face.meshes.map((m) => ({ index: m.index, blendshapeNames: m.blendshapeNames }));
    const { clips, missingShapeNames } = applyClipPack(pack, avatarMeshes);

    for (const c of clips) {
      const index = this.project.clips.reduce((max, cl) => Math.max(max, cl.index), -1) + 1;
      this.project.clips.push({ ...c, index, name: this.uniqueName(this.project.clips, c.name) });
    }
    if (clips.length > 0) this.persist();
    return { addedCount: clips.length, missingShapeNames };
  }

  // --- ジェスチャー --------------------------------------------------------

  assignGesture(hand: Hand, gesture: number, clipIndex: number | null): void {
    if (!this.project) return;
    this.project.gestures[hand][gesture] = clipIndex;
    this.previewing = { kind: "hand", hand, gesture };
    this.persist();
  }

  /** コンビネーションセルの割り当て。null は「セルなし」として配列から除去する。 */
  assignCombo(left: number, right: number, clipIndex: number | null): void {
    if (!this.project) return;
    const rest = this.project.combos.filter((c) => !(c.left === left && c.right === right));
    if (clipIndex !== null) rest.push({ left, right, clipIndex });
    rest.sort((a, b) => a.left - b.left || a.right - b.right);
    this.project.combos = rest;
    this.previewing = { kind: "combo", left, right };
    this.persist();
  }

  comboClipIndex(left: number, right: number): number | null {
    const c = this.project?.combos.find((x) => x.left === left && x.right === right);
    return c ? c.clipIndex : null;
  }

  /** 割り当てずにプレビューだけ切り替える */
  setPreviewTarget(target: PreviewTarget | null): void {
    this.previewing = target;
    this.notify();
  }

  /** プレビュー中の対象に割り当てられているクリップ */
  previewingClip(): FaceClip | null {
    const p = this.previewing;
    if (!p || !this.project) return null;
    if (p.kind === "clip") return this.clipByIndex(p.clipIndex);
    const index =
      p.kind === "hand"
        ? this.project.gestures[p.hand][p.gesture]
        : p.kind === "combo"
          ? this.comboClipIndex(p.left, p.right)
          : (this.project.menu.find((m) => m.index === p.index)?.clipIndex ?? null);
    return this.clipByIndex(index ?? null);
  }

  setApplyMode(mode: "ndmf" | "direct"): void {
    if (!this.project) return;
    this.project.applyMode = mode;
    this.persist();
  }

  // --- 書き出しオプション ----------------------------------------------------

  setApplyOption<K extends keyof ApplyOptions>(key: K, value: ApplyOptions[K]): void {
    if (!this.project) return;
    this.project.options = { ...(this.project.options ?? {}), [key]: value };
    this.persist();
  }

  // --- メニュー/トグル表情 ---------------------------------------------------

  /** メニュー項目を追加する。index は既存の最大値 +1（1 始まり）。 */
  addMenuEntry(clipIndex: number | null, label?: string): MenuEntry | null {
    if (!this.project) return null;
    if (this.project.menu.length >= 255) return null;
    const index = this.project.menu.reduce((max, m) => Math.max(max, m.index), 0) + 1;
    const clip = this.clipByIndex(clipIndex);
    const entry: MenuEntry = {
      index,
      label: (label ?? clip?.name ?? `表情${index}`).slice(0, 255),
      clipIndex,
    };
    this.project.menu.push(entry);
    this.previewing = { kind: "menu", index };
    this.persist();
    return entry;
  }

  updateMenuEntry(index: number, patch: Partial<Omit<MenuEntry, "index">>): void {
    if (!this.project) return;
    const m = this.project.menu.find((x) => x.index === index);
    if (!m) return;
    Object.assign(m, patch);
    this.persist();
  }

  removeMenuEntry(index: number): void {
    if (!this.project) return;
    this.project.menu = this.project.menu.filter((m) => m.index !== index);
    if (this.previewing?.kind === "menu" && this.previewing.index === index) this.previewing = null;
    this.persist();
  }

  /** メニュー項目の並び替え（表示順のみ。index は据え置き）。 */
  moveMenuEntry(index: number, delta: number): void {
    if (!this.project) return;
    const arr = this.project.menu;
    const i = arr.findIndex((m) => m.index === index);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    this.persist();
  }

  // --- キーフレーム ----------------------------------------------------------

  /** キーフレーム列を time 昇順に整え、durationSeconds を最終時刻に合わせる。 */
  private normalizeKeyframes(clip: FaceClip): void {
    const kfs = (clip.keyframes ?? []).slice().sort((a, b) => a.time - b.time);
    clip.keyframes = kfs;
    if (kfs.length > 0) {
      clip.durationSeconds = kfs[kfs.length - 1].time;
      // 静的プレビュー（ジェスチャー割り当て画面等）用の代表ポーズを
      // 常に最初のキーフレームと同期させておく（記録・移動・削除のたびに古い値が残らないように）。
      clip.shapes = kfs[0].shapes.map((s) => ({ ...s }));
    }
  }

  /** 現在のスライダー値を、指定時刻のキーフレームとしてクリップに記録する。 */
  addKeyframe(clipIndex: number, time: number): void {
    // SPEC_FMAPPLY: keyframes[].time は 0 以上。負や非有限の時刻は拒否する
    if (!Number.isFinite(time) || time < 0) return;
    const clip = this.clipByIndex(clipIndex);
    if (!clip) return;
    const shapes = [...this.editor.values.entries()]
      .map(([key, value]) => ({ ...parseShapeKey(key), value }))
      .filter((s) => s.value !== 0);
    if (!Array.isArray(clip.keyframes)) {
      // 静的クリップからの初回変換: 現在の shapes を t=0 のキーフレームにする
      clip.keyframes = [{ time: 0, shapes: clip.shapes.map((s) => ({ ...s })) }];
    }
    const existing = clip.keyframes.find((k) => Math.abs(k.time - time) < 1e-6);
    if (existing) existing.shapes = shapes;
    else clip.keyframes.push({ time, shapes });
    clip.type = "keyframe";
    this.normalizeKeyframes(clip);
    this.persist();
  }

  removeKeyframe(clipIndex: number, time: number): void {
    const clip = this.clipByIndex(clipIndex);
    if (!clip?.keyframes) return;
    clip.keyframes = clip.keyframes.filter((k) => Math.abs(k.time - time) >= 1e-6);
    if (clip.keyframes.length < 2) {
      // 2 枚未満になったら静的クリップへ戻す
      const first = clip.keyframes[0];
      if (first) clip.shapes = first.shapes.map((s) => ({ ...s }));
      delete clip.keyframes;
      clip.type = "static";
      clip.durationSeconds = 0.1;
    } else {
      this.normalizeKeyframes(clip);
    }
    this.persist();
  }

  /** キーフレームの内容をエディタに読み込む（編集して addKeyframe で上書きできる） */
  loadKeyframe(clipIndex: number, time: number): void {
    const clip = this.clipByIndex(clipIndex);
    const kf = clip?.keyframes?.find((k) => Math.abs(k.time - time) < 1e-6);
    if (!clip || !kf) return;
    this.editor.values = new Map(kf.shapes.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
    this.editor.editingClipIndex = clip.index;
    this.editor.tracking = trackingOf(clip);
    this.editor.keyframeTime = kf.time;
    this.tab = "expression";
    this.notify();
  }

  setKeyframeTime(t: number): void {
    this.editor.keyframeTime = t;
    const clip = this.clipByIndex(this.editor.editingClipIndex);
    if (clip?.type === "keyframe" && clip.keyframes && clip.keyframes.length > 0) {
      this.editor.values = this.sampleKeyframes(clip.keyframes, t);
    }
    this.notify();
  }

  /**
   * 指定時刻でのプレビュー値を、store を更新せずに算出する（ドラッグ中の軽量プレビュー用）。
   * keyframe クリップでなければ現在の editor.values のコピーを返す。
   */
  previewValuesAt(clipIndex: number, time: number): Map<string, number> {
    const clip = this.clipByIndex(clipIndex);
    if (clip?.type === "keyframe" && clip.keyframes && clip.keyframes.length > 0) {
      return this.sampleKeyframes(clip.keyframes, time);
    }
    return new Map(this.editor.values);
  }

  /** 任意のクリップの指定時刻での値を算出する（ジェスチャープレビューのアニメーション再生用）。 */
  sampleClipAt(clip: FaceClip, time: number): Map<string, number> {
    if (clip.type === "keyframe" && clip.keyframes && clip.keyframes.length > 0) {
      return this.sampleKeyframes(clip.keyframes, time);
    }
    return new Map(clip.shapes.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
  }

  /** 2 枚のキーフレームの間の時刻を直線補間した値を返す（前後どちらかしかなければそのまま）。 */
  private sampleKeyframes(kfs: ClipKeyframe[], time: number): Map<string, number> {
    const toMap = (shapes: BlendshapeValue[]): Map<string, number> =>
      new Map(shapes.filter((s) => s.value !== 0).map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
    if (time <= kfs[0].time) return toMap(kfs[0].shapes);
    const last = kfs[kfs.length - 1];
    if (time >= last.time) return toMap(last.shapes);

    let a = kfs[0];
    let b = last;
    for (let i = 0; i < kfs.length - 1; i++) {
      if (kfs[i].time <= time && time <= kfs[i + 1].time) {
        a = kfs[i];
        b = kfs[i + 1];
        break;
      }
    }
    const span = b.time - a.time;
    const ratio = span > 1e-9 ? (time - a.time) / span : 0;
    const aMap = new Map(a.shapes.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
    const bMap = new Map(b.shapes.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
    const keys = new Set<string>([...aMap.keys(), ...bMap.keys()]);
    const result = new Map<string, number>();
    for (const key of keys) {
      const av = aMap.get(key) ?? 0;
      const bv = bMap.get(key) ?? 0;
      const v = av + (bv - av) * ratio;
      if (v !== 0) result.set(key, v);
    }
    return result;
  }

  /**
   * 「使用中」表示の対象シェイプキー集合。keyframe クリップ編集中は、
   * 現在時刻の値が 0 でも他のキーフレームで使われていれば含める。
   */
  editorUsedShapeKeys(): Set<string> {
    const clip = this.clipByIndex(this.editor.editingClipIndex);
    if (clip?.type === "keyframe" && clip.keyframes) {
      const set = new Set<string>();
      for (const kf of clip.keyframes) {
        for (const s of kf.shapes) {
          if (s.value !== 0) set.add(shapeKeyOf(s.meshIndex, s.name));
        }
      }
      return set;
    }
    return new Set(this.editor.values.keys());
  }

  setClipLoop(clipIndex: number, loop: boolean): void {
    const clip = this.clipByIndex(clipIndex);
    if (!clip) return;
    clip.loop = loop;
    this.persist();
  }

  private persist(): void {
    void this.saveProject();
    this.notify();
  }

  /** プレビューに反映すべきシェイプ値（タブに応じて切り替える） */
  currentPreviewValues(): ReadonlyMap<string, number> {
    if (this.tab === "gesture" && this.previewing) {
      const clip = this.previewingClip();
      if (!clip) return new Map();
      return new Map(clip.shapes.map((s) => [shapeKeyOf(s.meshIndex, s.name), s.value]));
    }
    return this.editor.values;
  }
}

export const store = new Store();
